import { Injectable } from '@angular/core';
import { BaseService } from 'src/app/helpers/base.service';
import { environment } from 'src/environments/environment.development';
import { Observable, BehaviorSubject } from 'rxjs';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import * as signalR from '@microsoft/signalr';
import {
  Todo,
  Priority,
  ReminderType,
  RepeatInterval,
  AddTodoRequest,
  UpdateTodoRequest,
  TodoResponse,
  TodoReminder,
  iEmployeeResultDto,
} from 'src/app/helpers/Interfaces/todo';
import {
  iPaginationRequest,
  iPaginationResult,
} from 'src/app/helpers/Interfaces/pagination';

@Injectable({
  providedIn: 'root',
})
export class TodosService extends BaseService {
  private readonly _getAllEmployeesWithFiltration = `${environment.base_url}/api/Employee/GetAllWithFilteration`;
  private readonly _reminderHub = `${environment.base_url}/reminderHub`;
  private readonly _getAll = `${environment.base_url}/api/Todo/GetAll`;
  private readonly _getAllNew = `${environment.base_url}/api/Todo/GetAllNew`;
  private readonly _getById = `${environment.base_url}/api/Todo/GetBy`;
  private readonly _addNew = `${environment.base_url}/api/Todo/Add`;
  private readonly _update = `${environment.base_url}/api/Todo/Update`;
  private readonly _delete = `${environment.base_url}/api/Todo/Delete`;
  private readonly _markAsDone = `${environment.base_url}/api/Todo/MarkAsDone`;

  private readonly _getTodoDescription = `${environment.ai_url}/hr360/todos/generate-todo-description`;

  public reminderHub!: signalR.HubConnection;
  private remindersSubject = new BehaviorSubject<TodoReminder[]>([]);
  public reminders$ = this.remindersSubject.asObservable();

  constructor() {
    super();
    this.startReminderHub();
  }

  private initReminderHub(): void {
    this.reminderHub = new signalR.HubConnectionBuilder()
      .withUrl(this._reminderHub, {
        accessTokenFactory: () => {
          // console.log("Token sent to hub:", this._dataServ.tokenValue);
          return this._dataServ.tokenValue || '';
        },
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.reminderHub.on('ReceiveReminder', (data: any) => {
      const reminder: TodoReminder = {
        id: data.id,
        title: data.title,
        description: data.description,
        dueDate: data.dueDate,
        priority: data.priority,
      };

      const current = this.remindersSubject.value;
      this.remindersSubject.next([reminder, ...current]);
    });

    this.reminderHub.onreconnected(() => {
      // console.log('Reminder hub reconnected');
    });

    this.reminderHub.onclose(() => {
      // console.warn('Reminder hub disconnected');
    });
  }

  public async startReminderHub(): Promise<void> {
    if (!this.reminderHub) {
      this.initReminderHub();
    }

    if (this.reminderHub.state === signalR.HubConnectionState.Disconnected) {
      try {
        await this.reminderHub.start();
        // console.log('Reminder hub connected');
      } catch (err) {
        // console.error('Reminder hub connection failed', err);
        setTimeout(() => this.startReminderHub(), 5000);
      }
    }
  }

  getAllTodos(): Observable<Todo[]> {
    return this._http.get<Todo[]>(this._getAll);
  }

  getAllPaginated(payload: iPaginationRequest, headersData?: any) {
    return this._http.post<iPaginationResult<Todo>>(
      `${this._getAllNew}`,
      payload,
      {
        responseType: 'json',
        headers: headersData,
      },
    );
  }

  getTodoById(id: string): Observable<TodoResponse> {
    return this._http.get<TodoResponse>(`${this._getById}/${id}`);
  }

  addTodo(payload: AddTodoRequest): Observable<any> {
    return this._http.post(this._addNew, payload);
  }

  updateTodo(id: string, payload: UpdateTodoRequest): Observable<any> {
    return this._http.put(`${this._update}/${id}`, payload);
  }

  deleteTodo(id: string): Observable<any> {
    return this._http.delete(`${this._delete}/${id}`);
  }

  markAsDone(todoId: string, userId: string): Observable<any> {
    const params = new HttpParams().set('todoId', todoId).set('userId', userId);
    return this._http.post(this._markAsDone, null, { params });
  }

  remindLater(
    todoId: string,
    userId: string,
    remindDateTime: string,
  ): Observable<any> {
    const params = new HttpParams().set('todoId', todoId).set('userId', userId);

    return this._http.post(
      `${environment.base_url}/api/Todo/RemindLater`,
      `"${remindDateTime}"`,
      {
        params,
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  /* ===================== EMPLOYEES ===================== */

  getAllEmployeesWithFiltration(
    payload: any,
    isProfile: boolean = false,
  ): Observable<iEmployeeResultDto[]> {
    const headers = new HttpHeaders({
      companyId: environment.emptyGuid,
      stationId: environment.emptyGuid,
    });

    return this._http
      .post<any>(this._getAllEmployeesWithFiltration, payload, { headers })
      .pipe(
        map((res) =>
          res.data.map((emp: any) => {
            const employee: iEmployeeResultDto = { ...emp };
            employee.name = `${employee.firstName} ${employee.lastName}`;
            employee.Type = employee.type;

            if (
              employee.reportsToProfile &&
              !employee.reportsToProfile.includes(environment.base_url)
            ) {
              employee.reportsToProfile = `${environment.base_url}/${employee.reportsToProfile}`;
            }

            if (
              employee.profilePic &&
              !employee.profilePic.includes(environment.base_url)
            ) {
              employee.profilePic = `${environment.base_url}/${employee.profilePic}`;
            }

            return employee;
          }),
        ),
      );
  }

  /* ===================== REMINDER HELPERS ===================== */

  clearReminder(id: string): void {
    const updated = this.remindersSubject.value.filter((r) => r.id !== id);
    this.remindersSubject.next(updated);
  }

  getTodoDescription(payload: {
    title: string;
    priority: string;
    description: string;
  }) {
    return this._http.post<any>(this._getTodoDescription, payload, {
      responseType: 'json',
    });
  }
}
